'use client';

import { useState } from 'react';

import { Button, SubmitButton, Typography } from '@capsule/consumer-design-system';

import { Form, Formik } from 'formik';
import { useTranslations } from 'next-intl';
import { css } from 'styled-components';
import InlineSVG from 'svg-inline-react';
import * as Yup from 'yup';

import ClearRxLogo from 'public/clearrx_logo.svg';
import MobilePhonSVG from 'public/mobile_phone.svg';
import RxPadSVG from 'public/rx_pad.svg';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { FormikPhoneField } from 'src/app/components/Form/FormikPhoneField/FormikPhoneField';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import { useTriggerPharmacyHubDemoMutation } from 'src/app/hooks/data/pharmacyHub';
import capsuleValidation from 'src/app/utils/capsuleValidation';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import { ClearRxLayout } from '../../ClearRxLayout';
import { DemoEducationCard, DemoEducationContainer, DemoEducationTags } from '../styles';

const phoneInput = 'phone';
const patientNameInput = 'patientName';
const doctorNameInput = 'doctorName';

const defaultIconCSS = (padding = false) => css`
  padding: ${({ theme }) => (padding ? theme.space.s1 : '0')} !important;
  path {
    fill: currentColor;
  }
  svg {
    width: 100% !important;
    height: 100% !important;
  }
`;

export const PharmacyHubDemoTrigger = () => {
  const [isSubmitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const { mutateAsync: triggerDemo } = useTriggerPharmacyHubDemoMutation();
  const t = useTranslations('demoTrigger');
  const formValidationT = useTranslations('formValidation');

  const validationSchema = Yup.object({
    [phoneInput]: capsuleValidation.phone(formValidationT('mobilePhone')).required().default(''),
    [patientNameInput]: Yup.string().default(''),
    [doctorNameInput]: Yup.string().default(''),
  });
  const initialValues = validationSchema.cast({});

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting, setErrors }) => {
        setErrors({});
        setSubmitting(true);
        try {
          await triggerDemo({
            phone: values.phone,
            patient_name: !values[patientNameInput] ? null : values[patientNameInput],
            doctor_name: !values[doctorNameInput] ? null : values[doctorNameInput],
          });
          setSubmitSuccess(true);
        } catch {
          setErrors({
            [phoneInput]: t('form.errorMessage'),
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {formikBag => {
        const phoneIconCSS =
          formikBag.touched.phone && formikBag.errors.phone
            ? css`
                padding: ${({ theme }) => theme.space.s1} !important;
                path {
                  fill: rgb(231, 38, 8);
                }
                svg {
                  width: 100% !important;
                  height: 100% !important;
                }
              `
            : defaultIconCSS(true);

        return (
          <ClearRxLayout>
            {isSubmitSuccess && (
              <>
                <Typography variant="heading1">{t('success.title')}</Typography>
                <Typography
                  variant={'overline'}
                  css={css`
                    color: ${({ theme }) => theme.colors.capsuleGreen60};
                    margin-top: ${({ theme }) => theme.space.s6};
                    margin-bottom: ${({ theme }) => theme.space.s2};
                  `}
                >
                  {t('success.sectionTitle')}
                </Typography>
                <Typography
                  variant={'heading4'}
                  css={css`
                    margin-bottom: ${({ theme }) => theme.space.s4};
                  `}
                >
                  {t('success.subtitle')}
                </Typography>
                <DemoEducationContainer>
                  <DemoEducationCard>
                    <InlineSVG src={RxPadSVG} />
                    <Typography variant={'heading4'}>{t('success.card1Title')}</Typography>
                  </DemoEducationCard>
                  <DemoEducationCard>
                    <InlineSVG src={MobilePhonSVG} />
                    <Typography variant={'heading4'}>{t('success.card2Title')}</Typography>
                  </DemoEducationCard>
                  <DemoEducationCard>
                    <CapsuleIcon
                      name="pill_bottle"
                      iconString={CAPSULE_ICON_STRINGS.pill_bottle}
                      css={css`
                        width: ${({ theme }) => theme.space.s6};
                        height: ${({ theme }) => theme.space.s6};
                        margin-bottom: ${({ theme }) => theme.space.s2};
                      `}
                    />
                    <div>
                      <Typography
                        variant={'heading4'}
                        css={css`
                          margin-bottom: ${({ theme }) => theme.space.s2};
                        `}
                      >
                        {t('success.card3Title')}
                      </Typography>
                      <DemoEducationTags variant={'small'}>{t('success.tags.priorAuth')}</DemoEducationTags>
                      <DemoEducationTags variant={'small'}>{t('success.tags.outOfStock')}</DemoEducationTags>
                      <DemoEducationTags variant={'small'}>{t('success.tags.coupons')}</DemoEducationTags>
                      <DemoEducationTags variant={'small'}>{t('success.tags.insurance')}</DemoEducationTags>
                      <DemoEducationTags variant={'small'}>{t('success.tags.reducedHours')}</DemoEducationTags>
                    </div>
                  </DemoEducationCard>
                </DemoEducationContainer>
                <Button
                  onClick={() => {
                    formikBag.resetForm();
                    setSubmitSuccess(false);
                  }}
                  variant="outline-lg"
                  css={css`
                    margin-top: ${({ theme }) => theme.space.s6};
                  `}
                  isFullWidth
                >
                  {t('success.resetButton')}
                </Button>
              </>
            )}
            {!isSubmitSuccess && (
              <Form>
                <InlineSVG
                  src={ClearRxLogo}
                  css={css`
                    svg {
                      height: ${({ theme }) => theme.space.s6} !important;
                    }
                  `}
                />
                <Typography
                  variant="heading1"
                  css={css`
                    margin-top: ${({ theme }) => theme.space.s6};
                    margin-bottom: ${({ theme }) => theme.space.s4};
                  `}
                >
                  {t('form.title')}
                </Typography>
                <Typography
                  variant="body"
                  css={css`
                    margin-bottom: ${({ theme }) => theme.space.s6};
                  `}
                >
                  {t('form.description')}
                </Typography>
                {/*  */}
                <FormikPhoneField
                  adornment={{
                    left: <CapsuleIcon name="phone" iconString={CAPSULE_ICON_STRINGS.phone} css={phoneIconCSS} />,
                  }}
                  name={phoneInput}
                  label={{ isHidden: false, content: t('form.phoneLabel') }}
                />
                <FormikTextField
                  adornment={{
                    left: (
                      <CapsuleIcon
                        name="phone"
                        iconString={CAPSULE_ICON_STRINGS.checkout_photo}
                        css={defaultIconCSS()}
                      />
                    ),
                  }}
                  name={patientNameInput}
                  label={{
                    content: t('form.patientNameLabel'),
                    isHidden: false,
                  }}
                  type="text"
                  required={false}
                />
                <FormikTextField
                  adornment={{
                    left: (
                      <CapsuleIcon
                        name="phone"
                        iconString={CAPSULE_ICON_STRINGS.stethoscope_icon}
                        css={defaultIconCSS()}
                      />
                    ),
                  }}
                  name={doctorNameInput}
                  label={{
                    content: t('form.doctorNameLabel'),
                    isHidden: false,
                  }}
                  type="text"
                  required={false}
                />
                <SubmitButton
                  isSubmitting={formikBag.isSubmitting}
                  variant="contained-secondary-lg"
                  isFullWidth
                  css={css`
                    margin-top: ${({ theme }) => theme.space.s6};
                  `}
                >
                  {t('form.submitButton')}
                </SubmitButton>
              </Form>
            )}
          </ClearRxLayout>
        );
      }}
    </Formik>
  );
};
